// Fullscreen in the browser. The installed app (manifest: display "fullscreen") has no browser UI left
// to hide, and iPhone Safari offers no Fullscreen API for pages at all; in both cases the buttons stay
// hidden ("Zum Home-Bildschirm hinzufügen" is the way on the iPhone). None of this reaches the simulation.

export interface FullscreenEnv {
  /** the Fullscreen API exists and is allowed in this document */
  enabled: boolean;
  /** the document is fullscreen through the API right now */
  active: boolean;
  /** opened from the home screen: the manifest's display mode already hides the browser UI */
  standalone: boolean;
}

export type FullscreenAction = 'hidden' | 'enter' | 'exit';

export function fullscreenAction(env: FullscreenEnv): FullscreenAction {
  if (env.standalone || !env.enabled) return 'hidden';
  return env.active ? 'exit' : 'enter';
}

export const FULLSCREEN_LABEL: Record<Exclude<FullscreenAction, 'hidden'>, string> = { enter: 'Vollbild', exit: 'Vollbild verlassen' };

/** `Document`/`Window` as far as this file reads them, so tests can pass plain objects. */
export interface FullscreenDocument {
  fullscreenEnabled?: boolean;
  fullscreenElement: Element | null;
  documentElement: { requestFullscreen?: (options?: FullscreenOptions) => Promise<void> };
  exitFullscreen?: () => Promise<void>;
}
export interface FullscreenWindow {
  matchMedia(query: string): { matches: boolean };
  /** `standalone` is Safari's flag for "opened from the home screen" */
  navigator: { userAgent: string; standalone?: boolean };
}

export function readFullscreenEnv(doc: FullscreenDocument, win: FullscreenWindow): FullscreenEnv {
  const active = doc.fullscreenElement !== null;
  // "display-mode: fullscreen" also matches while the API holds the page fullscreen; only without
  // a fullscreen element does it mean the installed app
  const installed = win.matchMedia('(display-mode: standalone)').matches || (win.matchMedia('(display-mode: fullscreen)').matches && !active);
  return {
    enabled: doc.fullscreenEnabled === true && typeof doc.documentElement.requestFullscreen === 'function',
    active,
    standalone: installed || win.navigator.standalone === true,
  };
}

export async function toggleFullscreen(doc: FullscreenDocument): Promise<void> {
  try {
    if (doc.fullscreenElement) await doc.exitFullscreen?.();
    else await doc.documentElement.requestFullscreen?.({ navigationUI: 'hide' });
  } catch {
    // denied (no user gesture, embedding policy): the page simply stays as it is
  }
}

/** Makes `button` the fullscreen toggle; its label and visibility follow the document. */
export function bindFullscreenButton(button: HTMLButtonElement, doc: Document = document, win: Window = window): void {
  const refresh = (): void => {
    const action = fullscreenAction(readFullscreenEnv(doc, win));
    button.classList.toggle('hidden', action === 'hidden');
    if (action !== 'hidden') button.textContent = FULLSCREEN_LABEL[action];
  };
  button.addEventListener('click', () => void toggleFullscreen(doc));
  doc.addEventListener('fullscreenchange', refresh);
  refresh();
}
